import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { Subject, catchError, finalize, of, switchMap } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PermissionsService } from '../../../../core/services';
import { BadgeComponent, EmptyStateComponent, FeedbackMessageComponent } from '../../../../shared/components';
import { CatalogProductOptionInterface } from '../../interfaces';
import { BarcodeScannerService, CatalogProductService } from '../../services';

@Component({
  selector: 'app-product-lookup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    BadgeComponent,
    EmptyStateComponent,
    FeedbackMessageComponent
  ],
  templateUrl: './product-lookup.component.html',
  styleUrl: './product-lookup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductLookupComponent implements OnDestroy {
  @ViewChild('queryInput', { read: ElementRef }) private queryInputElement?: ElementRef<HTMLInputElement>;
  protected readonly query = new FormControl('', { nonNullable: true });
  protected readonly visible = signal(false);
  protected readonly loading = signal(false);
  protected readonly searched = signal(false);
  protected readonly error = signal(false);
  protected readonly products = signal<CatalogProductOptionInterface[]>([]);
  protected readonly permissions = inject(PermissionsService);
  private readonly catalog = inject(CatalogProductService);
  private readonly scanner = inject(BarcodeScannerService);
  private readonly searches = new Subject<string>();
  private releaseScanner: (() => void) | null = null;

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.searches.pipe(
      switchMap(query => {
        this.products.set([]);
        this.error.set(false);
        this.searched.set(!!query);
        if (!query) return of([]);
        this.loading.set(true);
        return this.catalog.search(query).pipe(
          catchError(() => { this.error.set(true); return of([]); }),
          finalize(() => this.loading.set(false))
        );
      }),
      takeUntilDestroyed(destroyRef)
    ).subscribe(products => this.products.set(products));
    inject(Router).events.pipe(takeUntilDestroyed(destroyRef)).subscribe(event => {
      if (event instanceof NavigationStart) this.close();
    });
  }

  ngOnDestroy(): void {
    this.releaseScanner?.();
  }

  @HostListener('window:keydown', ['$event'])
  protected handleShortcut(event: KeyboardEvent): void {
    if (event.key !== 'F3' || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) return;
    event.preventDefault();
    if (!this.visible()) this.open();
  }

  protected open(): void {
    if (!this.permissions.can('products', 'view') || this.visible()) return;
    this.visible.set(true);
    this.releaseScanner = this.scanner.captureScans(barcode => {
      this.query.setValue(barcode);
      this.search();
    });
  }

  protected onDialogShow(): void {
    setTimeout(() => {
      this.queryInputElement?.nativeElement?.focus();
    }, 0);
  }

  protected close(): void {
    this.visible.set(false);
    this.releaseScanner?.();
    this.releaseScanner = null;
    this.query.reset();
    this.searches.next('');
  }

  protected search(): void {
    const query = this.query.value.trim();
    if (!this.visible() || !query || !this.permissions.can('products', 'view')) return;
    this.searches.next(query);
  }
}
